/* ==========================================================================
   Boreal tiles: small custom elements for the homepage tile grid.
   <boreal-hero>    cross-fades 1-2 slides, dots + pause, respects reduced motion
   <boreal-finder>  live singles suggestions from /search/suggest.json
   <boreal-carousel> arrow buttons for carousel rows
   <boreal-schedule> re-orders the weekly schedule to start from today (Toronto)
   <boreal-personal> fills the customer row (recently viewed, because you viewed)
   No dependencies. Loaded with defer from layout/theme.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- Hero */

  if (!customElements.get('boreal-hero')) {
    customElements.define('boreal-hero', class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-slide]'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-dot]'));
        this.pauseBtn = this.querySelector('[data-pause]');
        this.index = 0;
        this.delay = parseInt(this.dataset.autoplay || '0', 10);
        this.paused = reduceMotion || !this.delay;
        this.userPaused = false;
        if (this.slides.length < 2) return;

        var self = this;
        this.dots.forEach(function (dot) {
          dot.addEventListener('click', function () {
            self.go(parseInt(dot.dataset.dot, 10));
            self.restart();
          });
        });

        if (this.pauseBtn) {
          if (reduceMotion) {
            this.pauseBtn.hidden = true;
          } else {
            this.pauseBtn.addEventListener('click', function () {
              self.userPaused = !self.userPaused;
              self.pauseBtn.classList.toggle('is-paused', self.userPaused);
              self.pauseBtn.setAttribute('aria-label', self.userPaused ? 'Play slides' : 'Pause slides');
              self.restart();
            });
          }
        }

        // Pause while hovered or while keyboard focus is inside.
        this.addEventListener('mouseenter', function () { self.hold = true; self.restart(); });
        this.addEventListener('mouseleave', function () { self.hold = false; self.restart(); });
        this.addEventListener('focusin', function () { self.hold = true; self.restart(); });
        this.addEventListener('focusout', function () { self.hold = false; self.restart(); });
        document.addEventListener('visibilitychange', function () { self.restart(); });

        // Theme editor: show the selected slide.
        this.addEventListener('shopify:block:select', function (e) {
          var i = self.slides.indexOf(e.target);
          if (i > -1) { self.hold = true; self.go(i); self.restart(); }
        });
        this.addEventListener('shopify:block:deselect', function () { self.hold = false; self.restart(); });

        this.restart();
      }

      disconnectedCallback() { clearInterval(this.timer); }

      go(i) {
        var n = this.slides.length;
        this.index = (i + n) % n;
        var active = this.index;
        this.slides.forEach(function (slide, k) {
          var on = k === active;
          slide.classList.toggle('is-active', on);
          slide.setAttribute('aria-hidden', on ? 'false' : 'true');
          var link = slide.querySelector('a');
          if (link) { if (on) link.removeAttribute('tabindex'); else link.setAttribute('tabindex', '-1'); }
        });
        this.dots.forEach(function (dot, k) {
          if (k === active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }

      restart() {
        clearInterval(this.timer);
        if (this.paused || this.userPaused || this.hold || document.hidden) return;
        var self = this;
        this.timer = setInterval(function () { self.go(self.index + 1); }, this.delay);
      }
    });
  }

  /* -------------------------------------------------------------- Finder */

  if (!customElements.get('boreal-finder')) {
    customElements.define('boreal-finder', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="q"]');
        this.list = this.querySelector('[role="listbox"]');
        this.status = this.querySelector('[data-status]');
        this.type = (this.dataset.type || '').trim();
        this.limit = parseInt(this.dataset.limit || '5', 10);
        this.active = -1;
        this.cache = {};
        if (!this.input || !this.list) return;

        try {
          this.money = new Intl.NumberFormat(this.dataset.locale || 'en-CA', {
            style: 'currency', currency: this.dataset.currency || 'CAD'
          });
        } catch (e) { this.money = null; }

        var self = this;
        this.input.addEventListener('input', function () {
          clearTimeout(self.debounce);
          self.debounce = setTimeout(function () { self.lookup(); }, 220);
        });
        this.input.addEventListener('keydown', function (e) { self.onKey(e); });
        this.input.addEventListener('focus', function () { if (self.list.children.length) self.open(); });
        document.addEventListener('click', function (e) { if (!self.contains(e.target)) self.close(); });
      }

      lookup() {
        var q = this.input.value.trim();
        if (q.length < 2) { this.close(); this.list.innerHTML = ''; return; }
        if (this.cache[q]) { this.render(q, this.cache[q]); return; }

        if (this.controller) this.controller.abort();
        this.controller = window.AbortController ? new AbortController() : null;

        var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        var url = root + 'search/suggest.json'
          + '?q=' + encodeURIComponent(q)
          + '&resources[type]=product'
          + '&resources[limit]=10'
          + '&resources[options][unavailable_products]=hide'
          + '&resources[options][fields]=title,product_type,variants.title';

        var self = this;
        fetch(url, this.controller ? { signal: this.controller.signal } : {})
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (!data) return;
            var items = ((data.resources || {}).results || {}).products || [];
            if (self.type) {
              items = items.filter(function (p) { return (p.type || '') === self.type; });
            }
            items = items.filter(function (p) { return p.available !== false; }).slice(0, self.limit);
            self.cache[q] = items;
            if (self.input.value.trim() === q) self.render(q, items);
          })
          .catch(function () { /* aborted or offline: keep the plain search form */ });
      }

      price(value) {
        var n = parseFloat(value);
        if (isNaN(n)) return '';
        return this.money ? this.money.format(n) : '$' + n.toFixed(2);
      }

      render(q, items) {
        var self = this;
        var html = '';
        items.forEach(function (p, i) {
          var img = p.image || (p.featured_image && p.featured_image.url) || '';
          if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=96';
          var title = self.escape(p.title);
          var setName = '';
          var m = /^(.*?)\s*\[(.+)\]\s*$/.exec(p.title || '');
          if (m) { title = self.escape(m[1]); setName = self.escape(m[2]); }
          var from = p.price_min && p.price_max && p.price_min !== p.price_max;
          html += '<a class="bt-suggest__item" role="option" id="' + self.list.id + '-' + i + '" href="' + p.url + '">'
            + '<span class="bt-suggest__img">' + (img ? '<img src="' + img + '" alt="" loading="lazy" width="48" height="48">' : '') + '</span>'
            + '<span class="bt-suggest__text"><span class="bt-suggest__name">' + title + '</span>'
            + (setName ? '<span class="bt-suggest__set">' + setName + '</span>' : '') + '</span>'
            + '<span class="bt-suggest__price">' + (from ? '<small>From </small>' : '') + self.price(p.price_min || p.price) + '</span>'
            + '</a>';
        });
        html += '<button type="button" class="bt-suggest__all" role="option" id="' + this.list.id + '-all">'
          + (items.length ? 'See all results for' : 'No in-stock matches yet. Search everything for')
          + ' &ldquo;' + this.escape(q) + '&rdquo;</button>';
        this.list.innerHTML = html;

        var allBtn = this.list.querySelector('.bt-suggest__all');
        allBtn.addEventListener('click', function (e) { e.preventDefault(); self.submit(); });

        this.active = -1;
        this.open();
        if (this.status) {
          this.status.textContent = items.length
            ? items.length + ' matching card' + (items.length === 1 ? '' : 's') + ' in stock. Use the arrow keys to browse.'
            : 'No in-stock matches.';
        }
      }

      onKey(e) {
        var options = Array.prototype.slice.call(this.list.querySelectorAll('[role="option"]'));
        if (e.key === 'Escape') { this.close(); return; }
        if (!options.length || this.list.hidden) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var step = e.key === 'ArrowDown' ? 1 : -1;
          this.active = (this.active + step + options.length) % options.length;
          options.forEach(function (o, i) { o.setAttribute('aria-selected', i === this.active ? 'true' : 'false'); }, this);
          this.input.setAttribute('aria-activedescendant', options[this.active].id);
          options[this.active].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && this.active > -1) {
          e.preventDefault();
          options[this.active].click();
        }
      }

      submit() {
        if (this.form.requestSubmit) this.form.requestSubmit(); else this.form.submit();
      }

      open() {
        this.list.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.list.hidden = true;
        this.active = -1;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }

      escape(s) {
        return String(s || '').replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }
    });
  }
  /* ------------------------------------------------------------ Carousel */

  if (!customElements.get('boreal-carousel')) {
    customElements.define('boreal-carousel', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        if (!this.track) return;
        var self = this;
        var page = function (dir) {
          var first = self.track.querySelector('.bt-tile:not([hidden])') || self.track.firstElementChild;
          var gap = parseFloat(getComputedStyle(self.track).columnGap) || 0;
          var tileW = first ? first.getBoundingClientRect().width + gap : self.track.clientWidth;
          var perPage = Math.max(1, Math.floor((self.track.clientWidth + gap) / tileW));
          self.track.scrollBy({ left: dir * perPage * tileW, behavior: reduceMotion ? 'auto' : 'smooth' });
        };
        if (this.prev) this.prev.addEventListener('click', function () { page(-1); });
        if (this.next) this.next.addEventListener('click', function () { page(1); });
        this.track.addEventListener('scroll', function () {
          cancelAnimationFrame(self.raf);
          self.raf = requestAnimationFrame(function () { self.update(); });
        }, { passive: true });
        if (window.ResizeObserver) {
          this.ro = new ResizeObserver(function () { self.update(); });
          this.ro.observe(this.track);
        } else {
          window.addEventListener('resize', function () { self.update(); });
        }
        // Theme editor: bring the selected tile into view.
        this.addEventListener('shopify:block:select', function (e) {
          var left = e.target.offsetLeft - self.track.offsetLeft;
          self.track.scrollTo({ left: left, behavior: 'auto' });
        });
        this.update();
      }

      disconnectedCallback() { if (this.ro) this.ro.disconnect(); }

      update() {
        var t = this.track;
        var max = t.scrollWidth - t.clientWidth;
        var overflow = max > 2;
        this.classList.toggle('has-overflow', overflow);
        if (this.prev) this.prev.disabled = !overflow || t.scrollLeft <= 2;
        if (this.next) this.next.disabled = !overflow || t.scrollLeft >= max - 2;
      }
    });
  }

  /* ------------------------------------------------------------ Schedule */

  if (!customElements.get('boreal-schedule')) {
    customElements.define('boreal-schedule', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        // Wait for children when the script runs before the markup is parsed.
        if (!this.querySelector('[data-sessions]')) {
          return requestAnimationFrame(function () { self.connectedCallback(); });
        }
        var today = this.torontoDay();
        if (!today || String(today) === this.dataset.today) return;
        var sessions;
        try { sessions = JSON.parse(this.querySelector('[data-sessions]').textContent); } catch (e) { return; }
        this.render(sessions, today);
      }

      torontoDay() {
        try {
          var name = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Toronto', weekday: 'short' }).format(new Date());
          return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(name) + 1;
        } catch (e) { return 0; }
      }

      render(sessions, today) {
        var short = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        var full = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        var max = parseInt(this.dataset.max || '7', 10);
        var esc = function (s) {
          return String(s || '').replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
          });
        };
        var html = '';
        var shown = 0;
        for (var offset = 0; offset < 7 && shown < max; offset++) {
          var day = ((today - 1 + offset) % 7) + 1;
          var first = true;
          for (var i = 0; i < sessions.length && shown < max; i++) {
            if (sessions[i].d !== day) continue;
            var label = offset === 0 ? 'Today' : full[day - 1];
            var dayCell = first
              ? (offset === 0 ? 'Today' : '<abbr title="' + full[day - 1] + '">' + short[day - 1] + '</abbr>')
              : '<span class="bt-visually-hidden">' + label + '</span>';
            html += '<li class="bt-sched__row' + (offset === 0 ? ' is-today' : '') + (first ? ' is-first' : '') + '">'
              + '<span class="bt-sched__day">' + dayCell + '</span>'
              + '<span class="bt-sched__name">' + esc(sessions[i].n) + '</span>'
              + '<span class="bt-sched__time">' + esc(sessions[i].t) + '</span></li>';
            first = false;
            shown++;
          }
        }
        var list = this.querySelector('[data-list]');
        if (list) list.innerHTML = html;
        this.dataset.today = String(today);
      }
    });
  }
  /* ------------------------------------------- Hide past events (cached pages) */

  var hidePast = function () {
    var now = Date.now() / 1000;
    Array.prototype.forEach.call(document.querySelectorAll('.boreal-tiles [data-hide-after]'), function (el) {
      if (parseInt(el.getAttribute('data-hide-after'), 10) < now && !(window.Shopify && Shopify.designMode)) el.hidden = true;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hidePast); else hidePast();
  /* ------------------------------------------------------- Customer row */
  // <boreal-personal data-kind="recent|because"> fills a tile from this
  // browser's recently viewed list (localStorage 'bt-recent', written on
  // product pages). Tiles stay hidden until they have enough products.

  var readRecent = function () {
    try { return JSON.parse(localStorage.getItem('bt-recent') || '[]').filter(function (p) { return p && p.h; }); }
    catch (e) { return []; }
  };
  var shopRoot = function () { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; };
  var parseItems = function (html) {
    var box = document.createElement('div');
    box.innerHTML = html;
    return Array.prototype.slice.call(box.querySelectorAll('li.bt-tile__product'));
  };
  var revealRow = function (el) {
    var row = el.closest('.boreal-tiles[data-hide-empty]');
    if (row) row.hidden = false;
  };

  if (!customElements.get('boreal-personal')) {
    customElements.define('boreal-personal', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        if (!this.querySelector('[data-personal-list]')) {
          return requestAnimationFrame(function () { self.connectedCallback(); });
        }
        if (this.started) return;
        this.started = true;
        this.tile = this.closest('[data-personal-tile]');
        this.list = this.querySelector('[data-personal-list]');
        this.min = parseInt(this.dataset.min || '2', 10);
        var kind = this.dataset.kind;
        var job;
        if (kind === 'cart') {
          job = this.cart();
        } else {
          var recent = readRecent();
          if (!recent.length) return this.done([]);
          job = kind === 'because' ? this.because(recent) : kind === 'game' ? this.game(recent) : this.recent(recent);
        }
        job.then(function (items) { self.done(items); }).catch(function () { self.done([]); });
      }

      recent(recent) {
        var root = shopRoot();
        var picks = recent.slice(0, 8);
        return Promise.all(picks.map(function (p) {
          return fetch(root + 'products/' + encodeURIComponent(p.h) + '?view=bt-mini', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : ''; })
            .catch(function () { return ''; });
        })).then(function (pages) {
          var items = [];
          pages.forEach(function (html) {
            parseItems(html).forEach(function (li) { if (li.dataset.available === 'true') items.push(li); });
          });
          return items.slice(0, 4);
        });
      }

      because(recent) {
        var last = recent[0];
        var seen = {};
        recent.forEach(function (p) { seen[p.h] = true; });
        var titleEl = this.tile && this.tile.querySelector('[data-personal-title]');
        if (titleEl && last.t) titleEl.textContent = (this.dataset.titlePrefix || 'Because you viewed') + ' ' + this.shortName(last.t);
        var url = shopRoot() + 'recommendations/products?product_id=' + encodeURIComponent(last.id) +
          '&limit=10&intent=related&section_id=boreal-recs';
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            return parseItems(html).filter(function (li) { return !seen[li.dataset.handle]; }).slice(0, 4);
          });
      }

      // Cart: /cart.js items as tile products, plus subtotal and checkout.
      cart() {
        var self = this;
        var currency = this.dataset.currency || 'CAD';
        var fmt;
        try { fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency }); } catch (e) { fmt = null; }
        var money = function (cents) { var n = (cents || 0) / 100; return fmt ? fmt.format(n) : '$' + n.toFixed(2); };
        var esc = function (v) {
          return String(v || '').replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
          });
        };
        return fetch(shopRoot() + 'cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cart) {
            if (!cart || !cart.item_count) return [];
            var summary = self.tile && self.tile.querySelector('[data-cart-summary]');
            if (summary) {
              var countEl = summary.querySelector('[data-cart-count]');
              var subEl = summary.querySelector('[data-cart-subtotal]');
              if (countEl) countEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' · ';
              if (subEl) subEl.textContent = money(cart.total_price);
              summary.hidden = false;
            }
            return cart.items.slice(0, 4).map(function (item) {
              var img = item.image ? item.image + (item.image.indexOf('?') > -1 ? '&' : '?') + 'width=300' : '';
              var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
              var li = document.createElement('li');
              li.className = 'bt-tile__product';
              li.innerHTML = '<a class="bt-mini" href="' + esc(item.url) + '">'
                + '<span class="bt-mini__media">' + (img ? '<img class="bt-mini__img" src="' + esc(img) + '" alt="" loading="lazy" width="300" height="300">' : '') + '</span>'
                + '<span class="bt-mini__name">' + esc(item.product_title) + (variant ? ' · ' + esc(variant) : '') + '</span>'
                + '<span class="bt-mini__price">' + money(item.final_line_price) + (item.quantity > 1 ? '<span class="bt-mini__from"> × ' + item.quantity + '</span>' : '') + '</span>'
                + '</a>';
              return li;
            });
          });
      }

      // Game: most-viewed product type (singles and tickets ignored), newest in stock.
      game(recent) {
        var labels = {
          'MTG Sealed': 'Magic: The Gathering', 'Pokemon Sealed': 'Pokémon', 'Lorcana Sealed': 'Disney Lorcana',
          'One Piece Sealed': 'ONE PIECE', 'Star Wars: Unlimited Sealed': 'Star Wars: Unlimited',
          'Union Arena TCG Sealed': 'Union Arena', 'Yugioh Sealed': 'Yu-Gi-Oh!', 'Gundam Card Game': 'Gundam',
          'Digimon Card Game': 'Digimon', 'Riftbound: League of Legends': 'Riftbound', 'Soul Masters Sealed': 'Soul Masters',
          'Board Game': 'board games', 'Party Game': 'party games', 'Wargaming': 'wargaming', 'Roleplaying Game': 'RPGs',
          'Dungeons & Dragons': 'Dungeons & Dragons', 'Card Sleeves': 'card sleeves', 'Deck Boxes': 'deck boxes',
          'Playmats': 'playmats', 'Dice': 'dice', 'Puzzle': 'puzzles', 'Pop! Vinyl Figures': 'Funko Pop!'
        };
        var counts = {};
        recent.forEach(function (p, i) {
          var t = p.ty;
          if (!t || /single|event|ticket/i.test(t)) return;
          counts[t] = (counts[t] || 0) + (12 - i); // recent views weigh more
        });
        var type = Object.keys(counts).sort(function (a, b) { return counts[b] - counts[a]; })[0];
        if (!type) return Promise.resolve([]);
        var seen = {};
        recent.forEach(function (p) { seen[p.h] = true; });
        var base = shopRoot() + 'collections/types?q=' + encodeURIComponent(type) + '&sort_by=created-descending';
        var titleEl = this.tile && this.tile.querySelector('[data-personal-title]');
        if (titleEl) titleEl.textContent = (this.dataset.titlePrefix || 'New in') + ' ' + (labels[type] || type);
        var more = this.tile && this.tile.querySelector('[data-game-more]');
        if (more) { more.href = base; more.hidden = false; }
        return fetch(base + '&view=bt-quad', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) { return parseItems(html).filter(function (li) { return !seen[li.dataset.handle]; }).slice(0, 4); });
      }

      // "Catharsis [Lorwyn Eclipsed]" -> "Catharsis"; long names cut at a word, max ~34 chars.
      shortName(t) {
        var n = String(t).replace(/\s*\[.*?\]\s*/g, ' ').replace(/\s*\((?:\d+\s*(?:packs?|decks?|cards?))\)\s*/ig, ' ').replace(/\s+/g, ' ').trim();
        if (n.length <= 34) return n;
        var cut = n.slice(0, 34);
        var sp = cut.lastIndexOf(' ');
        return (sp > 18 ? cut.slice(0, sp) : cut).replace(/[\s:,\-–]+$/, '') + '…';
      }

      done(items) {
        if (items.length >= this.min) {
          this.list.innerHTML = '';
          this.list.classList.remove('bt-tile__products--loading');
          var list = this.list;
          items.forEach(function (li) { list.appendChild(li); });
          if (this.tile) this.tile.hidden = false;
          revealRow(this);
        } else if (!(window.Shopify && Shopify.designMode) && this.tile) {
          this.tile.hidden = true;
        }
      }
    });
  }

  // Server-rendered customer tiles (Buy it again) reveal their row immediately.
  var revealServerTiles = function () {
    Array.prototype.forEach.call(document.querySelectorAll('.boreal-tiles[data-hide-empty] [data-personal-tile]:not([hidden])'), revealRow);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealServerTiles); else revealServerTiles();
})();
